import React from "react";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Sparkles, ArrowRight, Scale } from "lucide-react";

export default function Hero({ onCtaClick }) {
  return (
    <section
      id="top"
      className="relative overflow-hidden bg-gradient-to-b from-slate-50 to-white"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 grain-overlay pointer-events-none" />
      <div
        className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full opacity-20 blur-3xl -translate-y-1/3 translate-x-1/3"
        style={{ backgroundColor: "#2DD4BF" }}
        aria-hidden="true"
      />
      <div
        className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full opacity-10 blur-3xl translate-y-1/3 -translate-x-1/3"
        style={{ backgroundColor: "#2563EB" }}
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div
              className="inline-flex items-center gap-2 rounded-full bg-blue-50 border border-blue-100 px-3 py-1.5 text-xs font-semibold text-blue-700"
              data-testid="hero-badge"
            >
              <Scale className="h-3.5 w-3.5" />
              Intermediario de créditos · Chile
            </div>

            <h1
              className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]"
              data-testid="hero-title"
            >
              Compara opciones de crédito
              <span className="block text-blue-600">
                desde $500.000 CLP
              </span>
            </h1>

            <p
              className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl"
              data-testid="hero-subtitle"
            >
              Recibe ofertas de distintas entidades financieras en un solo lugar.
              Tasa referencial <strong className="text-slate-900">desde 1% mensual</strong>,
              plazos desde 61 días. Tú eliges la opción que más te conviene.
            </p>

            <div
              className="mt-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 max-w-2xl"
              data-testid="hero-intermediary-disclaimer"
            >
              <strong>Aviso importante:</strong> Bancoorp actúa como
              intermediario y <strong>no otorga directamente los créditos</strong>.
              La aprobación y las condiciones finales dependen de la entidad
              financiera.
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={onCtaClick}
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-7 py-6 text-base font-semibold group"
                data-testid="hero-primary-cta"
              >
                Comparar créditos
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="rounded-full px-7 py-6 text-base border-slate-300 hover:bg-slate-100"
                data-testid="hero-secondary-cta"
              >
                <a href="#como-funciona">Cómo funciona</a>
              </Button>
            </div>

            <div className="mt-8 grid grid-cols-3 gap-4 max-w-lg text-xs sm:text-sm">
              <div className="flex items-center gap-2 text-slate-600">
                <ShieldCheck className="h-4 w-4 text-teal-500 shrink-0" />
                Sin costo para ti
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <Sparkles className="h-4 w-4 text-teal-500 shrink-0" />
                Comparación clara
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <Scale className="h-4 w-4 text-teal-500 shrink-0" />
                100% transparente
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div
              className="relative rounded-2xl border border-slate-200 bg-white shadow-xl p-6 lg:p-8"
              data-testid="hero-card"
            >
              <div className="flex items-center justify-between mb-6">
                <span className="text-xs font-bold uppercase tracking-widest text-blue-600">
                  Tasa referencial
                </span>
                <span className="text-xs text-slate-500">CLP · Chile</span>
              </div>
              <div className="flex items-end gap-2">
                <span className="text-6xl font-extrabold tracking-tight text-slate-900">
                  1%
                </span>
                <span className="text-lg font-medium text-slate-500 pb-2">
                  mensual desde
                </span>
              </div>
              <p className="mt-2 text-xs text-slate-500">
                La tasa final depende del perfil del cliente y la entidad financiera.
              </p>

              <div className="mt-6 grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                  <div className="text-xs text-slate-500">Monto desde</div>
                  <div className="text-xl font-bold text-slate-900">$500.000</div>
                </div>
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                  <div className="text-xs text-slate-500">Plazo mínimo</div>
                  <div className="text-xl font-bold text-slate-900">61 días</div>
                </div>
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                  <div className="text-xs text-slate-500">Ofertas</div>
                  <div className="text-xl font-bold text-slate-900">Múltiples</div>
                </div>
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                  <div className="text-xs text-slate-500">Evaluación</div>
                  <div className="text-xl font-bold text-slate-900">Gratuita</div>
                </div>
              </div>

              <div className="mt-6 text-[11px] text-slate-500 leading-relaxed border-t border-slate-200 pt-4">
                Las cifras mostradas son referenciales. Las condiciones finales (tasa,
                CAE, cuotas y costo total) son entregadas por la entidad financiera
                que apruebe tu crédito.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
